import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { RolloverRequest } from '../../../core/models/rollover-request.model';
import { RolloverService } from '../../../core/services/api-services/rollover.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PageEvent, PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { ApprovedRollover } from './models/approved-rollover';
import { RolloverDetailView } from './models/rollover-detail-view';
import { FilterPanelComponent } from "../../../shared/components/filter-panel/filter-panel.component";
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import e from 'express';

declare var bootstrap: any;

@Component({
  selector: 'app-rollover-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PaginatorComponent, BreadcrumbComponent, FilterPanelComponent],
  templateUrl: './rollover-management.component.html',
  styleUrl: './rollover-management.component.css'
})
export class RolloverManagementComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  // ───────────────────────────────────────────────────────────────
  // ⬛ ViewChild References
  // ───────────────────────────────────────────────────────────────
  @ViewChild('rolloverRequestOffCanvasEl', { static: false }) rolloverRequestOffCanvasRef!: ElementRef;
  @ViewChild('rolloverProfileOffcanvasEl', { static: false }) rolloverProfileOffcanvasRef!: ElementRef;


  // ───────────────────────────────────────────────────────────────
  // ⬛ Component State - Rollover Requests Tab
  // ───────────────────────────────────────────────────────────────
  rolloverRequests: RolloverRequest[] = [];
  filteredRolloverRequests: RolloverRequest[] = [];
  selectedRollover: RolloverRequest | null = null;
  requestViewForm!: FormGroup;

  // ───────────────────────────────────────────────────────────────
  // ⬛ Component State - Approved Rollovers Tab
  // ───────────────────────────────────────────────────────────────
  rollovers: ApprovedRollover[] = [];
  filteredRollovers: ApprovedRollover[] = [];
  filteredApprovedRollovers: ApprovedRollover[] = [];
  pagedRollovers: ApprovedRollover[] = [];

  pagedRolloverRequests: RolloverRequest[] = [];

  selectedRolloverProfile: RolloverDetailView | null = null;
  profileLoading = false;
  profileError = '';

  // ───────────────────────────────────────────────────────────────
  // ⬛ Common State
  // ───────────────────────────────────────────────────────────────
  isLoadingRollovers = false;
  isLoadingRequests = false;
  errorRollovers = '';
  errorRequests = '';
  searchText: string = '';
  searchRolloverRequestsText: string = '';

  // Offcanvas instances
  private userFormOffcanvasInstance: any = null;
  private rolloverProfileOffcanvasInstance: any = null;

  dataSubscriber$!: Subscription;

  // Approved Rollovers tab
  currentRolloverPage = 1;
  rolloverPageSize = 10;
  totalRollovers = 0;

  // Rollover Requests tab
  currentRequestPage = 1;
  requestPageSize = 10;
  totalRequests = 0;

  FilterValue: string = '';

  // =============== Server filter Props =============== //

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CompanyId',
      options: [
        {
          value: 'CompnayId',
          label: 'Compnay ID',
          field: {
            type: 'text',
            controlName: 'CompanyId',            // key sent to backend
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        }
      ],
    },
  ];

  // =================================================== //

  // ───────────────────────────────────────────────────────────────
  // ⬛ Constructor
  // ───────────────────────────────────────────────────────────────
  constructor(
    private toaster: ToastrService,
    private rolloverService: RolloverService,
    private fb: FormBuilder,
    private breadcrumbService: BreadcrumbService,
    protected authService: AuthService
  ) {
    this.dataSubscriber$ = new Subscription();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Lifecycle Hook
  // ───────────────────────────────────────────────────────────────
  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Credit & Risk', icon: 'bi bi-shield-exclamation' },
      { label: 'Manage Rollovers', icon: 'bi bi-arrow-repeat' }
    ]);

    this.initForm();
    this.errorRollovers = '';
    this.errorRequests = '';
    this.fetchRolloverRequests(this.currentRequestPage, this.requestPageSize);
    this.fetchApprovedRollovers(this.currentRolloverPage, this.rolloverPageSize);
  }

  ngAfterViewInit(): void {
    // Offcanvas instances will be initialized when first opened
    // This prevents conflicts with Bootstrap's auto-initialization
  }

  initForm(): void {
    this.requestViewForm = this.fb.group({
      companyRollOverId: [{ value: '', disabled: true }],
      companyId: [{ value: '', disabled: true }],
      companyName: [{ value: '', disabled: true }],
      rollOverPercentageId: [{ value: '', disabled: true }],
      rollOverPercentage: [{ value: '', disabled: true }],
      rollOverDayId: [{ value: '', disabled: true }],
      rollOverDay: [{ value: '', disabled: true }],
      totalDueAmount: [{ value: '', disabled: true }],
      originalDueDate: [{ value: '', disabled: true }],
      rollOverAmount: [{ value: '', disabled: true }],
      nonRollOverAmount: [{ value: '', disabled: true }],
      rollOverFeeAmount: [{ value: '', disabled: true }],
      immediatePayableAmount: [{ value: '', disabled: true }],
      rolloverDueDate: [{ value: '', disabled: true }],
      isPaid: [{ value: '', disabled: true }],
      isApproved: [{ value: '', disabled: true }],
      extensionId: [{ value: '', disabled: true }],
      isActive: [{ value: '', disabled: true }],
      createdDate: [{ value: '', disabled: true }],
      status: [{ value: '', disabled: true }],
    });
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ API Calls - Rollover Requests
  // ───────────────────────────────────────────────────────────────

  fetchRolloverRequests(page: number, pageSize: number, companyId?: string): void {
    this.isLoadingRequests = true;
    this.dataSubscriber$.add(
      this.rolloverService.getRolloverRequestsPaginated(page, pageSize, companyId).subscribe({
        next: (response) => {
          if (response.success) {
            this.rolloverRequests = response.data;
            this.filteredRolloverRequests = [...this.rolloverRequests];
            this.pagedRolloverRequests = [...this.rolloverRequests];
            this.totalRequests = response.totalCount;
            this.currentRequestPage = response.pageNumber;
            this.requestPageSize = response.pageSize;
            this.errorRequests = '';
          } else {
            this.isLoadingRequests = false;
            this.rolloverRequests = [];
            this.filteredRolloverRequests = [];
            this.pagedRolloverRequests = [];
            this.errorRequests = response?.message || 'Failed to load rollover requests.';
          }
          this.isLoadingRequests = false;
        },
        error: (err) => {
          this.isLoadingRequests = false;
          this.rolloverRequests = [];
          this.filteredRolloverRequests = [];
          this.pagedRolloverRequests = [];
          this.errorRequests = 'API error: ' + err.message;
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ API Calls - Approved Rollovers
  // ───────────────────────────────────────────────────────────────

  fetchApprovedRollovers(page: number, pageSize: number, companyId?: string): void {
    this.isLoadingRollovers = true;
    this.dataSubscriber$.add(
      this.rolloverService.getApprovedRolloversPaginated(page, pageSize, companyId).subscribe({
        next: (response) => {
          if (response.success) {
            this.rollovers = response.data;
            this.filteredRollovers = [...this.rollovers];
            this.pagedRollovers = [...this.rollovers];   // already the page slice
            this.totalRollovers = response.totalCount;
            this.currentRolloverPage = response.pageNumber;
            this.rolloverPageSize = response.pageSize;
            this.errorRollovers = '';
          } else {
            this.isLoadingRollovers = false;
            this.rollovers = [];
            this.filteredRollovers = [];
            this.pagedRollovers = [];
            this.errorRollovers = response?.message || 'Failed to load approved rollovers.';
          }
          this.isLoadingRollovers = false;
        },
        error: (err) => {
          this.isLoadingRollovers = false;
          this.rollovers = [];
          this.filteredRollovers = [];
          this.pagedRollovers = [];
          this.errorRollovers = 'API error: ' + err.message;
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Offcanvas Handlers - Request View
  // ───────────────────────────────────────────────────────────────
  openRequestView(request: RolloverRequest): void {
    this.selectedRollover = request;
    const formattedRequest = {
      ...request,
      originalDueDate: request.originalDueDate ? request.originalDueDate.split('T')[0] : '',
      rolloverDueDate: request.rolloverDueDate ? request.rolloverDueDate.split('T')[0] : '',
      createdDate: request.createdDate ? request.createdDate.split('T')[0] : ''
    };
    this.requestViewForm.patchValue(formattedRequest);
    this.requestViewForm.disable();

    // Initialize and show offcanvas if not already initialized
    if (!this.userFormOffcanvasInstance && this.rolloverRequestOffCanvasRef?.nativeElement) {
      try {
        this.userFormOffcanvasInstance = new bootstrap.Offcanvas(
          this.rolloverRequestOffCanvasRef.nativeElement
        );
      } catch (error) {
        console.error('Error initializing offcanvas:', error);
        return;
      }
    }

    // Show the offcanvas
    if (this.userFormOffcanvasInstance) {
      this.userFormOffcanvasInstance.show();
    }
  }

  closeRequestViewOffcanvas(): void {
    if (this.userFormOffcanvasInstance) {
      this.userFormOffcanvasInstance.hide();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Offcanvas Handlers - Rollover Profile
  // ───────────────────────────────────────────────────────────────
  openRolloverProfile(companyRollOverId: number): void {
    if (!companyRollOverId) {
      this.profileError = 'Invalid rollover ID.';
      return;
    }

    this.profileLoading = true;
    this.profileError = '';
    this.selectedRolloverProfile = null;

    this.dataSubscriber$.add(
      this.rolloverService.getDetailsViewOfRolloverRequest(companyRollOverId)
        .pipe(finalize(() => this.profileLoading = false))
        .subscribe({
          next: (response) => {
            if (response?.success && response.data) {
              this.selectedRolloverProfile = response.data;

              // Initialize and show offcanvas if not already initialized
              if (!this.rolloverProfileOffcanvasInstance && this.rolloverProfileOffcanvasRef?.nativeElement) {
                try {
                  this.rolloverProfileOffcanvasInstance = new bootstrap.Offcanvas(
                    this.rolloverProfileOffcanvasRef.nativeElement
                  );
                } catch (error) {
                  console.error('Error initializing profile offcanvas:', error);
                  return;
                }
              }

              // Show the offcanvas
              if (this.rolloverProfileOffcanvasInstance) {
                this.rolloverProfileOffcanvasInstance.show();
              }
            } else {
              this.profileError = response?.message || 'Failed to load rollover profile.';
            }
          },
          error: (err) => {
            this.profileError = 'API error: ' + (err?.message || 'Unknown error');
          }
        })
    );
  }

  closeRolloverProfileOffcanvas(): void {
    if (this.rolloverProfileOffcanvasInstance) {
      this.rolloverProfileOffcanvasInstance.hide();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Search Functionality
  // ───────────────────────────────────────────────────────────────
  performSearch(): void {
    const s = this.searchText.trim().toLowerCase();
    const sr = this.searchRolloverRequestsText.trim().toLowerCase();

    this.filteredRollovers = s
      ? this.rollovers.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(s)))
      : [...this.rollovers];

    this.filteredRolloverRequests = sr
      ? this.rolloverRequests.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(sr)))
      : [...this.rolloverRequests];

    this.pagedRollovers = [...this.filteredRollovers];
    this.pagedRolloverRequests = [...this.filteredRolloverRequests];
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Pagination
  // ───────────────────────────────────────────────────────────────
  private updateRolloverPage(): void {
    // This will be handled by your paginator component
    this.pagedRollovers = this.filteredRollovers.slice(0, 10);
  }

  private updateRolloverRequestsPage(): void {
    // This will be handled by your paginator component
    this.pagedRolloverRequests = this.filteredRolloverRequests.slice(0, 10);
  }

  // onRolloverPageChange(paginatedData: ApprovedRollover[]): void {
  //   this.pagedRollovers = paginatedData;
  // }
  onRolloverPageChange(event: PageEvent): void {
    this.currentRolloverPage = event.page;
    this.rolloverPageSize = event.pageSize;
    this.fetchApprovedRollovers(event.page, event.pageSize);
  }

  // onRolloverRequestsPageChange(paginatedData: RolloverRequest[]): void {
  //   this.pagedRolloverRequests = paginatedData;
  // }
  onRolloverRequestsPageChange(event: PageEvent): void {
    this.currentRequestPage = event.page;
    this.requestPageSize = event.pageSize;
    this.fetchRolloverRequests(event.page, event.pageSize);
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Request Actions
  // ───────────────────────────────────────────────────────────────
  RejectRequest(): void {
    const id = this.selectedRollover?.companyRollOverId;
    if (!id) {
      this.toaster.error('Please select a rollover request before rejecting.', 'No Request Selected');
      return;
    }

    const rejectionData = {
      companyRollOverId: id,
      isApproved: false,
      rejectionReason: 'Rejected by user'
    };

    this.rolloverService.authorizeRollOverRequest(id, rejectionData).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toaster.success('The rollover request has been rejected successfully.', 'Request Rejected');
          this.fetchRolloverRequests(this.currentRequestPage, this.requestPageSize);
          this.closeRequestViewOffcanvas();
        } else {
          this.toaster.warning(response?.message || 'Unable to reject the rollover request.', 'Rejection Failed');
          console.error('Failed to reject request:', response?.message);
        }
      },
      error: (err) => {
        this.toaster.error('An error occurred while rejecting the rollover request. Please try again.', 'Error');
        console.error('Error while rejecting request:', err);
      }
    });
  }

  ApproveRequest(): void {
    const id = this.selectedRollover?.companyRollOverId;
    if (!id) {
      this.toaster.error('Please select a rollover request before approving.', 'No Request Selected');
      return;
    }

    const approvalData = {
      companyRollOverId: id,
      isApproved: true
    };

    this.rolloverService.authorizeRollOverRequest(id, approvalData).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toaster.success('The rollover request has been approved successfully.', 'Request Approved');
          this.fetchRolloverRequests(this.currentRequestPage, this.requestPageSize); // Refresh rollover requests list
          this.fetchApprovedRollovers(this.currentRolloverPage, this.rolloverPageSize); // Refresh approved rollovers list
          this.closeRequestViewOffcanvas();
        } else {
          this.toaster.warning(response?.message || 'Unable to approve the rollover request.', 'Approval Failed');
          console.error('Failed to approve request:', response?.message);
        }
      },
      error: (err) => {
        this.toaster.error('An error occurred while approving the rollover request. Please try again.', 'Error');
        console.error('Error while approving request:', err);
      }
    });
  }

  // =============================================================================================== //
  // Server Side Filtration
  // =============================================================================================== //

  onFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.FilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.currentRolloverPage = 1;
    this.searchText = '';

    this.fetchApprovedRollovers(this.currentRolloverPage, this.rolloverPageSize, filterValue);
  }

  onClear(): void {
    this.currentRolloverPage = 1;
    this.searchText = '';
    this.FilterValue = '';
    this.errorRollovers = '';
    this.fetchApprovedRollovers(this.currentRolloverPage, this.rolloverPageSize);
  }

  ngOnDestroy(): void {
    // Dispose offcanvas instances
    if (this.userFormOffcanvasInstance) {
      this.userFormOffcanvasInstance.dispose();
    }
    if (this.rolloverProfileOffcanvasInstance) {
      this.rolloverProfileOffcanvasInstance.dispose();
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.dataSubscriber$.unsubscribe();
  }
}